/**
 * Performance Monitoring Utilities
 * 
 * Implements Core Web Vitals tracking and performance metrics collection
 * for the PWA application. Provides real-time performance monitoring
 * and reporting capabilities.
 */

import { onCLS, onFCP, onLCP, onTTFB, onINP, type Metric } from 'web-vitals';

// Performance thresholds based on Core Web Vitals
export const PERFORMANCE_THRESHOLDS = {
  // Core Web Vitals thresholds (good/needs improvement/poor)
  LCP: { good: 2500, poor: 4000 }, // Largest Contentful Paint (ms)
  FID: { good: 100, poor: 300 },   // First Input Delay (ms) - deprecated but still tracked
  INP: { good: 200, poor: 500 },   // Interaction to Next Paint (ms) - replaces FID
  CLS: { good: 0.1, poor: 0.25 },  // Cumulative Layout Shift (score)
  
  // Other important metrics
  FCP: { good: 1800, poor: 3000 }, // First Contentful Paint (ms)
  TTFB: { good: 800, poor: 1800 }, // Time to First Byte (ms)
  
  // Bundle size thresholds
  BUNDLE_SIZE: { good: 500 * 1024, poor: 1024 * 1024 }, // 500KB / 1MB
} as const;

// Performance metric types
export interface PerformanceMetrics {
  lcp?: number;
  fid?: number;
  inp?: number;
  cls?: number;
  fcp?: number;
  ttfb?: number;
  timestamp: number;
  url: string;
  userAgent: string;
}

export interface BundleMetrics {
  totalSize: number;
  chunkSizes: Record<string, number>;
  loadTime: number;
  cacheHitRate: number;
  timestamp: number;
}

// Performance data storage
class PerformanceStore {
  private metrics: PerformanceMetrics[] = [];
  private bundleMetrics: BundleMetrics[] = [];
  private listeners: ((metrics: PerformanceMetrics) => void)[] = [];

  addMetric(metric: PerformanceMetrics): void {
    this.metrics.push(metric);
    
    // Keep only last 100 metrics to prevent memory issues
    if (this.metrics.length > 100) {
      this.metrics = this.metrics.slice(-100);
    }
    
    // Notify listeners
    this.listeners.forEach(listener => listener(metric));
  }

  addBundleMetric(metric: BundleMetrics): void {
    this.bundleMetrics.push(metric);
    
    // Keep only last 50 bundle metrics
    if (this.bundleMetrics.length > 50) {
      this.bundleMetrics = this.bundleMetrics.slice(-50);
    }
  }

  getMetrics(): PerformanceMetrics[] {
    return [...this.metrics];
  }

  getBundleMetrics(): BundleMetrics[] {
    return [...this.bundleMetrics];
  }

  getLatestMetrics(): PerformanceMetrics | null {
    return this.metrics.length > 0 ? this.metrics[this.metrics.length - 1] : null;
  }

  subscribe(listener: (metrics: PerformanceMetrics) => void): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  clear(): void {
    this.metrics = [];
    this.bundleMetrics = [];
  }
}

// Global performance store
export const performanceStore = new PerformanceStore();

// Performance metric classification
export function classifyMetric(value: number, thresholds: { good: number; poor: number }): 'good' | 'needs-improvement' | 'poor' {
  if (value <= thresholds.good) return 'good';
  if (value <= thresholds.poor) return 'needs-improvement';
  return 'poor';
}

// Calculate performance score (0-100)
export function calculatePerformanceScore(metrics: Partial<PerformanceMetrics>): number {
  let score = 100;
  let totalWeight = 0;

  // LCP (25% weight)
  if (metrics.lcp !== undefined) {
    const lcpScore = metrics.lcp <= PERFORMANCE_THRESHOLDS.LCP.good ? 100 :
                    metrics.lcp <= PERFORMANCE_THRESHOLDS.LCP.poor ? 75 : 50;
    score += lcpScore * 0.25;
    totalWeight += 0.25;
  }

  // INP (25% weight) - prioritize over FID
  if (metrics.inp !== undefined) {
    const inpScore = metrics.inp <= PERFORMANCE_THRESHOLDS.INP.good ? 100 :
                    metrics.inp <= PERFORMANCE_THRESHOLDS.INP.poor ? 75 : 50;
    score += inpScore * 0.25;
    totalWeight += 0.25;
  } else if (metrics.fid !== undefined) {
    // Fallback to FID if INP not available
    const fidScore = metrics.fid <= PERFORMANCE_THRESHOLDS.FID.good ? 100 :
                    metrics.fid <= PERFORMANCE_THRESHOLDS.FID.poor ? 75 : 50;
    score += fidScore * 0.25;
    totalWeight += 0.25;
  }

  // CLS (25% weight)
  if (metrics.cls !== undefined) {
    const clsScore = metrics.cls <= PERFORMANCE_THRESHOLDS.CLS.good ? 100 :
                    metrics.cls <= PERFORMANCE_THRESHOLDS.CLS.poor ? 75 : 50;
    score += clsScore * 0.25;
    totalWeight += 0.25;
  }

  // FCP (15% weight)
  if (metrics.fcp !== undefined) {
    const fcpScore = metrics.fcp <= PERFORMANCE_THRESHOLDS.FCP.good ? 100 :
                    metrics.fcp <= PERFORMANCE_THRESHOLDS.FCP.poor ? 75 : 50;
    score += fcpScore * 0.15;
    totalWeight += 0.15;
  }

  // TTFB (10% weight)
  if (metrics.ttfb !== undefined) {
    const ttfbScore = metrics.ttfb <= PERFORMANCE_THRESHOLDS.TTFB.good ? 100 :
                     metrics.ttfb <= PERFORMANCE_THRESHOLDS.TTFB.poor ? 75 : 50;
    score += ttfbScore * 0.10;
    totalWeight += 0.10;
  }

  // Normalize score based on available metrics
  return totalWeight > 0 ? Math.round(score / totalWeight) : 0;
}

// Web Vitals reporting function
function reportWebVital(metric: Metric): void {
  const performanceMetric: Partial<PerformanceMetrics> = {
    timestamp: Date.now(),
    url: window.location.href,
    userAgent: navigator.userAgent,
  };

  // Map web vitals to our metrics
  switch (metric.name) {
    case 'LCP':
      performanceMetric.lcp = metric.value;
      break;
    case 'INP':
      performanceMetric.inp = metric.value;
      break;
    case 'CLS':
      performanceMetric.cls = metric.value;
      break;
    case 'FCP':
      performanceMetric.fcp = metric.value;
      break;
    case 'TTFB':
      performanceMetric.ttfb = metric.value;
      break;
  }

  // Store the metric
  const existingMetrics = performanceStore.getLatestMetrics();
  const updatedMetrics: PerformanceMetrics = {
    ...existingMetrics,
    ...performanceMetric,
    timestamp: Date.now(),
    url: window.location.href,
    userAgent: navigator.userAgent,
  };

  performanceStore.addMetric(updatedMetrics);

  // Log in development
  if (import.meta.env.MODE === 'development') {
    console.log(`[Performance] ${metric.name}:`, metric.value, classifyMetric(
      metric.value,
      PERFORMANCE_THRESHOLDS[metric.name as keyof typeof PERFORMANCE_THRESHOLDS] || { good: 0, poor: Infinity }
    ));
  }

  // Send to analytics in production (placeholder)
  if (import.meta.env.MODE === 'production') {
    // TODO: Send to analytics service
    // analytics.track('web_vital', {
    //   metric: metric.name,
    //   value: metric.value,
    //   rating: metric.rating,
    //   url: window.location.href
    // });
  }
}

// Initialize Web Vitals tracking
export function initializePerformanceMonitoring(): void {
  // Track Core Web Vitals
  onCLS(reportWebVital);
  onFCP(reportWebVital);
  onLCP(reportWebVital);
  onTTFB(reportWebVital);
  onINP(reportWebVital);

  // Track bundle loading performance
  if (window.performance && window.performance.getEntriesByType) {
    const navigationEntries = window.performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (navigationEntries.length > 0) {
      const navigation = navigationEntries[0];
      
      // Calculate bundle load time
      const loadTime = navigation.loadEventEnd - navigation.fetchStart;
      
      // Estimate bundle size from resource timing
      const resourceEntries = window.performance.getEntriesByType('resource') as PerformanceResourceTiming[];
      const jsResources = resourceEntries.filter(entry => 
        entry.name.includes('.js') && entry.name.includes('/assets/')
      );
      
      const chunkSizes: Record<string, number> = {};
      let totalSize = 0;
      
      jsResources.forEach(resource => {
        // Estimate size from transfer size (if available)
        const size = resource.transferSize || resource.encodedBodySize || 0;
        const filename = resource.name.split('/').pop() || 'unknown';
        chunkSizes[filename] = size;
        totalSize += size;
      });

      const bundleMetric: BundleMetrics = {
        totalSize,
        chunkSizes,
        loadTime,
        cacheHitRate: jsResources.filter(r => r.transferSize === 0).length / jsResources.length,
        timestamp: Date.now(),
      };

      performanceStore.addBundleMetric(bundleMetric);
    }
  }

  // Performance observer for additional metrics
  if ('PerformanceObserver' in window) {
    try {
      // Observe long tasks (>50ms)
      const longTaskObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.duration > 50) {
            console.warn(`[Performance] Long task detected: ${entry.duration}ms`);
          }
        });
      });
      longTaskObserver.observe({ entryTypes: ['longtask'] });

      // Observe layout shifts
      const layoutShiftObserver = new PerformanceObserver((list) => {
        let clsValue = 0;
        list.getEntries().forEach((entry) => {
          if (!(entry as any).hadRecentInput) {
            clsValue += (entry as any).value;
          }
        });
        
        if (clsValue > 0) {
          console.log(`[Performance] Layout shift: ${clsValue}`);
        }
      });
      layoutShiftObserver.observe({ entryTypes: ['layout-shift'] });
    } catch (error) {
      console.warn('[Performance] PerformanceObserver not fully supported:', error);
    }
  }
}

// Export performance data
export function exportPerformanceData(): {
  metrics: PerformanceMetrics[];
  bundleMetrics: BundleMetrics[];
  summary: {
    averageScore: number;
    totalMeasurements: number;
    lastMeasurement: PerformanceMetrics | null;
  };
} {
  const metrics = performanceStore.getMetrics();
  const bundleMetrics = performanceStore.getBundleMetrics();
  
  const scores = metrics
    .filter(m => m.lcp || m.fcp || m.cls || m.inp || m.fid)
    .map(m => calculatePerformanceScore(m));
  
  const averageScore = scores.length > 0 
    ? scores.reduce((sum, score) => sum + score, 0) / scores.length 
    : 0;

  return {
    metrics,
    bundleMetrics,
    summary: {
      averageScore: Math.round(averageScore),
      totalMeasurements: metrics.length,
      lastMeasurement: performanceStore.getLatestMetrics(),
    },
  };
}

// Performance monitoring hook for React components
export function usePerformanceMonitoring() {
  const [currentMetrics, setCurrentMetrics] = React.useState<PerformanceMetrics | null>(null);
  const [performanceScore, setPerformanceScore] = React.useState<number>(0);

  React.useEffect(() => {
    const unsubscribe = performanceStore.subscribe((metrics) => {
      setCurrentMetrics(metrics);
      setPerformanceScore(calculatePerformanceScore(metrics));
    });

    // Set initial values
    const latest = performanceStore.getLatestMetrics();
    if (latest) {
      setCurrentMetrics(latest);
      setPerformanceScore(calculatePerformanceScore(latest));
    }

    return unsubscribe;
  }, []);

  return {
    currentMetrics,
    performanceScore,
    exportData: exportPerformanceData,
    classifyMetric,
  };
}

// Add React import for the hook
import React from 'react';